import classNames from "classnames";
import { find, some } from "lodash";
import { Calendar } from "PFTypes";
import React, { memo, useCallback, useMemo } from "react";

import { useCalendarContext } from "../../context/calendar_context";
import { addOneDay, getDaysDifference, isDateBetween } from "../calendar.utils";
import css from "./days.module.scss";

interface NominalDaysProps {
  rowCalendarsData: {
    calendarsUsed: string[];
    calendars: Record<string, Calendar>;
  };
  startDate: string;
  endDate: string;
}

type CalendarWithEndDate = Calendar & {
  endDate: string;
  startDate: string;
};

const isWorkingDay = (calendar: Calendar, day: Date) =>
  some(calendar.nominal_days, (nominalDay) => nominalDay.wday === day.getDay() && nominalDay.working);

const NominalDays = memo(({ rowCalendarsData, startDate, endDate }: NominalDaysProps) => {
  const { dayWidth } = useCalendarContext();
  const { calendars, calendarsUsed } = rowCalendarsData;
  const startDateObject = new Date(startDate);

  const calendarsWithEndDates: CalendarWithEndDate[] = useMemo(
    () =>
      calendarsUsed
        .sort((date1, date2) => getDaysDifference(new Date(date2), new Date(date1)))
        .map((calKey, i) => ({
          ...calendars[calKey],
          endDate: calendarsUsed[i + 1] || endDate,
          startDate: calKey
        })),
    [calendars, calendarsUsed, endDate]
  );

  const getActiveCalendar = useCallback(
    (day: Date) =>
      find(calendarsWithEndDates, (cal) =>
        isDateBetween(day, new Date(cal.startDate), new Date(cal.endDate))
      ),
    [calendarsWithEndDates]
  );

  const nonWorkingDates = useMemo(() => {
    const dates: Date[] = [];
    const endDateObject = new Date(endDate);
    for (let day = new Date(startDate); day < endDateObject; addOneDay(day)) {
      const activeCalendar = getActiveCalendar(day);

      if (!activeCalendar) {
        continue;
      }

      if (!isWorkingDay(activeCalendar, day)) {
        dates.push(new Date(day.getTime()));
      }
    }

    return dates;
  }, [endDate, getActiveCalendar, startDate]);

  return (
    <div className={css.nominalDays}>
      {nonWorkingDates.map((date, i) => (
        <div
          key={`${date.getTime()}-nominal-day-${i}`}
          className={classNames(css.day, css.nominalDay)}
          style={{
            width: dayWidth - 1,
            left: Math.floor(getDaysDifference(startDateObject, date)) * dayWidth + 1
          }}
        />
      ))}
    </div>
  );
});

NominalDays.displayName = "NominalDays";

export default NominalDays;
